import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ObjectType } from '@econtractor/types';
import * as apiObjects from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import vconst from 'utils/helpers/structs/vconst';
import SolarIcon from 'components/icons/SolarIcon';

export interface ObjectSelectedProps {
    value?: ObjectType | null;
    onChange?: (object: ObjectType | null) => void;
    title?: string;
    rightElement?: ReactNode;
    maxHeight?: number;
    className?: string;
    disabled?: boolean;
    onPage?: number;
    clearable?: boolean;
    /** Лише перегляд вибраного об'єкта (без пошуку та очищення) */
    viewOnly?: boolean;
    /** Статуси для пошуку; якщо не передано — усі статуси */
    statuses?: number[];
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
}

const ObjectNameItem = ({ object }: { object: ObjectType }) => {
    const title = object.objectName || object.clientName || '—';
    return (
        <div className="min-w-0 overflow-hidden flex-grow-1">
            <h6 className="mb-1 text-truncate" title={title}>
                {title}
            </h6>
            {object.objectName && object.clientName ? (
                <p className="text-muted f-12 mb-0 text-truncate" title={object.clientName}>
                    {object.clientName}
                </p>
            ) : object.teamName ? (
                <p className="text-muted f-12 mb-0 text-truncate" title={object.teamName}>
                    {object.teamName}
                </p>
            ) : null}
        </div>
    );
};

const ObjectSelected = ({
    value = null,
    onChange,
    title,
    rightElement = null,
    maxHeight = 220,
    className = '',
    disabled = false,
    onPage = 1000,
    clearable = true,
    viewOnly = false,
    statuses,
    preload = false,
}: ObjectSelectedProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [objects, setObjects] = useState<ObjectType[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);

    if (preload) return null;

    const isInteractive = !viewOnly && !disabled;
    const canClear = clearable && isInteractive && Boolean(onChange);
    const isFixed = Boolean(value) || viewOnly;
    const showResults = !viewOnly && (loading || searched);
    const statusFilter =
        statuses && statuses.length > 0 ? statuses : vconst.objectAllStatuses;

    const fetchObjects = async () => {
        if (!isInteractive || loading || Boolean(value)) return;

        setLoading(true);
        const res = await apiObjects.getList({
            search: (search || '').trim(),
            page: 1,
            onPage,
            status: statusFilter,
        });
        setLoading(false);
        setSearched(true);

        if (!res) {
            setObjects([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setObjects(res.objects || []);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchObjects();
        }
    };

    const handleSelect = (object: ObjectType) => {
        if (!isInteractive || !onChange) return;
        onChange(object);
        setSearch('');
        setObjects([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (!canClear || !onChange) return;
        onChange(null);
        setSearch('');
        setObjects([]);
        setSearched(false);
    };

    const labelText = title || t('selectTitle');

    return (
        <div className={`user-selected ${className || 'mb-3'}`.trim()}>
            {(labelText || rightElement) && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className={`form-label text-muted mb-1 ${labelText ? '' : 'd-none'}`}>
                        {labelText}
                    </label>
                    {rightElement}
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        {value ? (
                            <ObjectNameItem object={value} />
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </div>
                    {canClear && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="user-selected-clear"
                            title={t('common:buttons.clear')}
                            onClick={handleClear}
                        >
                            <SolarIcon name="x-lg" />
                        </Button>
                    )}
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            disabled={disabled || loading}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={fetchObjects}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && objects.length === 0 && (
                                <div className="user-selected-empty">
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && objects.length > 0 && (
                                <ul className="user-selected-list">
                                    {objects.map((object) => (
                                        <li key={object.ID}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(object)}
                                            >
                                                <ObjectNameItem object={object} />
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ObjectSelected;
